import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ChatSession, ProgressTree, ProgressStep, ConnectionState } from '@localagent/sdk';
import type { SessionEvent, ContextUpdateData, TaskCompletedData } from '@localagent/sdk';
import type { ContextSnapshot, SessionContextSummary, ContextResolvedData, CompactionEvent, TaskStatus, TaskFinalUsage } from '../types/context-types';
import type { ClarificationQuestion, MediaActionApprovalRequest, PlatformApprovalRequest } from '@localagent/sdk';

export interface ClarificationSubmission {
  questionId: string;
  selectedOption?: string | null;
  openResponse?: string | null;
  skipped: boolean;
}

export type ExecutionState = 'idle' | 'running';

export interface SessionEventsState {
  events: SessionEvent[];
  isReasoning: boolean;
  executionState: ExecutionState;
  lastStopReason: string | null;
  workflowId: string | null;
  recentSteps: ProgressStep[];
  pendingClarification: ClarificationQuestion | null;
  pendingPlatformApproval: PlatformApprovalRequest | null;
  pendingMediaActionApproval: MediaActionApprovalRequest | null;
  contextUsagePercent: number;
  sessionContext: SessionContextSummary | null;
  pinChangeCounter: number;
  connectionState: ConnectionState;
  error: string | null;
  /** workspace_state's typed admission refusal (workspace_state ADR-028). */
  workspaceRefusal: string | null;
  reasoningRuns: ProgressTree[];
}

export function useSessionEvents(
  chat: ChatSession | null,
): SessionEventsState & {
  sendMessage: (content: string, mentions?: import('@localagent/sdk').MentionRef[], viewContext?: import('@localagent/sdk').ViewContext, attachments?: import('@localagent/sdk').ImageAttachment[]) => void;
  sendClarificationResponse: (response: ClarificationSubmission) => void;
  respondToMediaActionApproval: (approvalId: string, approved: boolean) => void;
  stopExecution: () => void;
  redirectExecution: (content: string) => void;
  chatSession: ChatSession | null;
} {
  const queryClient = useQueryClient();
  const chatSessionRef = useRef<ChatSession | null>(null);
  const [, forceUpdate] = useState(0);
  const rerender = useCallback(() => forceUpdate(n => n + 1), []);

  useEffect(() => {
    chatSessionRef.current = chat;
    if (!chat) {
      rerender();
      return;
    }

    const unsubscribers: Array<() => void> = [
      chat.onMessage(() => {
        void queryClient.invalidateQueries({
          queryKey: ['sessions', chat.workspaceId ?? null, chat.sessionId, 'messages'],
        });
        rerender();
      }),
      chat.onStateChange(rerender),
      chat.onProgressUpdate(rerender),
      chat.onConnectionStateChange(rerender),
      chat.onClarificationRequest(rerender),
      chat.onError(rerender),
    ];
    rerender();

    return () => {
      for (const unsubscribe of unsubscribers) {
        unsubscribe();
      }
    };
  }, [chat, rerender, queryClient]);

  const events = (chat?.sessionEvents ?? []) as unknown as SessionEvent[];
  const reasoningRuns = chat?.reasoningRuns ?? [];
  const isReasoning = chat?.state === 'reasoning' || chat?.state === 'starting';
  const recentSteps = chat?.recentSteps ?? [];
  const pendingClarification = chat?.pendingClarification ?? null;
  const pendingPlatformApproval = chat?.pendingPlatformApproval ?? null;
  const pendingMediaActionApproval = chat?.pendingMediaActionApproval ?? null;
  const connectionState = chat?.connectionState ?? 'disconnected';
  const error = chat?.progressTree?.error ?? null;
  const workspaceRefusal = chat?.progressTree?.workspaceRefusal ?? null;
  const lastStopReason = chat?.lastStopReason ?? null;
  const executionState: ExecutionState = isReasoning ? 'running' : 'idle';

  const { sessionContext, pinChangeCounter } = useMemo(
    () => deriveContextState(events),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [events.length],
  );

  const sendMessage = useCallback((
    content: string,
    mentions?: import('@localagent/sdk').MentionRef[],
    viewContext?: import('@localagent/sdk').ViewContext,
    attachments?: import('@localagent/sdk').ImageAttachment[],
  ) => {
    chatSessionRef.current?.send(content, mentions, viewContext, attachments);
  }, []);

  const sendClarificationResponse = useCallback((response: ClarificationSubmission) => {
    chatSessionRef.current?.respondToClarification({
      questionId: response.questionId,
      selectedOption: response.selectedOption ?? '',
      skipped: response.skipped,
    });
  }, []);


  const respondToMediaActionApproval = useCallback((approvalId: string, approved: boolean) => {
    chatSessionRef.current?.respondToMediaActionApproval({ approvalId, approved });
  }, []);

  const stopExecution = useCallback(() => {
    try {
      chatSessionRef.current?.stop();
    } catch {
      /* surfaced via ChatSession.onError */
    }
  }, []);

  const redirectExecution = useCallback((content: string) => {
    try {
      chatSessionRef.current?.redirect(content);
    } catch {
      /* surfaced via ChatSession.onError */
    }
  }, []);

  return {
    events,
    isReasoning,
    executionState,
    lastStopReason,
    workflowId: null,
    recentSteps,
    pendingClarification,
    pendingPlatformApproval,
    pendingMediaActionApproval,
    contextUsagePercent: 0,
    sessionContext,
    pinChangeCounter,
    connectionState,
    error,
    workspaceRefusal,
    reasoningRuns,
    sendMessage,
    sendClarificationResponse,
    respondToMediaActionApproval,
    stopExecution,
    redirectExecution,
    chatSession: chat,
  };
}

const RUN_CLOSE_EVENTS = new Set(['reasoning_completed', 'reasoning_failed', 'execution_stopped']);

interface TaskLifecycle {
  status: TaskStatus;
  finalUsage?: TaskFinalUsage;
}

function deriveTaskLifecycles(events: readonly SessionEvent[]): Map<string, TaskLifecycle> {
  const lifecycles = new Map<string, TaskLifecycle>();
  let runClosed = false;

  for (const e of events) {
    const data = e.data as Record<string, unknown>;
    const id = data.id as string | undefined;
    switch (e.event_type) {
      case 'task_started':
        if (id && !lifecycles.has(id)) lifecycles.set(id, { status: 'active' });
        break;
      case 'task_completed':
      case 'task_failed': {
        if (!id) break;
        const d = e.data as unknown as TaskCompletedData;
        const status: TaskStatus = e.event_type === 'task_failed' ? 'failed' : 'completed';
        let finalUsage: TaskFinalUsage | undefined;
        if (d.usage && typeof d.usage.total_tokens === 'number') {
          finalUsage = {
            totalTokens: d.usage.total_tokens,
            promptTokens: d.usage.prompt_tokens ?? 0,
            completionTokens: d.usage.completion_tokens ?? 0,
            contextTotalTokens: d.context_total_tokens ?? 0,
            contextWindowSize: d.context_window_size ?? 0,
            contextUsagePercent: d.context_usage_percent ?? 0,
          };
        }
        lifecycles.set(id, { status, finalUsage });
        break;
      }
      default:
        if (RUN_CLOSE_EVENTS.has(e.event_type)) runClosed = true;
    }
  }

  if (runClosed) {
    for (const [id, lc] of lifecycles) {
      if (lc.status === 'active') lifecycles.set(id, { ...lc, status: 'completed' });
    }
  }

  return lifecycles;
}

function snapshotFromUpdate(e: SessionEvent, lifecycle?: TaskLifecycle): ContextSnapshot {
  const d = e.data as unknown as ContextUpdateData;
  const hasUsage = typeof d.prompt_tokens === 'number' && (d.context_window_size ?? 0) > 0;
  return {
    iteration: d.iteration ?? 0,
    parentId: d.parent_id,
    status: lifecycle?.status ?? 'active',
    usagePercent: hasUsage ? (d.usage_percent ?? null) : null,
    promptTokens: hasUsage ? (d.prompt_tokens ?? null) : null,
    totalTokens: hasUsage ? (d.total_tokens ?? null) : null,
    contextWindowSize: (d.context_window_size ?? 0) > 0 ? d.context_window_size : null,
    attribution: d.attribution ?? null,
    overflowHandled: d.overflow_handled ?? false,
    messagesSummarized: d.messages_summarized ?? 0,
    tokensReduced: d.tokens_reduced ?? 0,
    entries: d.in_context_entries ?? [],
    supersededEntries: d.superseded_entries ?? [],
    compactedEntries: d.compacted_entries ?? [],
    messageCount: d.message_count ?? 0,
    timestamp: e.created_at,
    taskId: d.id,
    personaName: d.persona_name,
    staleMessageCount: d.stale_message_count ?? 0,
    tokensSavedByDedup: d.tokens_saved_by_dedup ?? 0,
    finalUsage: lifecycle?.finalUsage,
  };
}

export function deriveContextState(
  events: readonly SessionEvent[],
): {
  sessionContext: SessionContextSummary | null;
  pinChangeCounter: number;
} {
  const lifecycles = deriveTaskLifecycles(events);

  const allSnapshots: ContextSnapshot[] = [];
  let parentContext: ContextSnapshot | null = null;
  for (const e of events) {
    const data = e.data as Record<string, unknown>;
    if (e.event_type === 'task_started' && !data.parent_id) {
      parentContext = null;
    } else if (e.event_type === 'context_update') {
      const id = data.id as string | undefined;
      const snap = snapshotFromUpdate(e, id ? lifecycles.get(id) : undefined);
      allSnapshots.push(snap);
      if (!snap.parentId) parentContext = snap;
    }
  }

  let pinChangeCounter = 0;
  for (const e of events) {
    if (e.event_type === 'tool_call_result') {
      const toolName = e.data.tool_name as string | undefined;
      if (toolName === 'context_pin' || toolName === 'context_unpin') {
        pinChangeCounter++;
      }
    }
  }

  const sessionContext = deriveSessionContext(allSnapshots, parentContext, events);

  return { sessionContext, pinChangeCounter };
}

function deriveSessionContext(
  allSnapshots: ContextSnapshot[],
  parentContext: ContextSnapshot | null,
  events: readonly SessionEvent[],
): SessionContextSummary | null {
  if (allSnapshots.length === 0) return null;

  const taskContexts = new Map<string, ContextSnapshot>();
  for (const snap of allSnapshots) {
    const key = snap.taskId ?? snap.personaName ?? '__planner__';
    taskContexts.set(key, snap);
  }

  let sessionSpendTokens = 0;
  for (const snap of taskContexts.values()) {
    if (snap.finalUsage) sessionSpendTokens += snap.finalUsage.totalTokens;
    else if (snap.totalTokens != null) sessionSpendTokens += snap.totalTokens;
  }

  const compactionEvents: CompactionEvent[] = [];
  for (const snap of allSnapshots) {
    if (snap.overflowHandled && snap.messagesSummarized > 0) {
      compactionEvents.push({
        taskId: snap.taskId ?? '__planner__',
        personaName: snap.personaName ?? 'unknown',
        messagesSummarized: snap.messagesSummarized,
        tokensReduced: snap.tokensReduced,
        timestamp: snap.timestamp,
        compactedEntries: snap.compactedEntries,
      });
    }
  }

  const contextResolved: ContextResolvedData[] = [];
  for (const e of events) {
    if (e.event_type === 'context_resolved') {
      const taskId = e.data.id as string | undefined;
      contextResolved.push({
        total_resolved_messages: (e.data.total_resolved_messages as number) ?? 0,
        ctx_injection_breakdown: (e.data.ctx_injection_breakdown as Record<string, number>) ?? undefined,
        persona_name: (e.data.persona_name as string) ?? 'unknown',
        task_id: taskId,
      });
    }
  }

  return {
    taskContexts,
    parentContext,
    sessionSpendTokens,
    compactionEvents,
    contextResolved,
  };
}
